import { Controller } from "@hotwired/stimulus"

/**
 * Full-screen camera for the chat: take a photo or record a video, then preview it with a caption
 * before sending. The captured file is handed over through a `camera-capture:captured` event
 * ({ file, caption }), which rich-form turns into a regular media message.
 *
 * When the camera cannot be opened in the page (no getUserMedia, permission refused, no camera),
 * a file input with `capture` opens the device's own camera app instead and the preview stays the same.
 */

// MP4 first: it is what every platform plays back. WebM is the fallback for browsers that
// cannot record MP4 (Firefox, older Chrome). The file is labelled with the recorder's real type.
const VIDEO_MIME_TYPES = [
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm"
]

const VIDEO_EXTENSIONS = {
  "video/mp4": "mp4",
  "video/webm": "webm"
}

// Keeps a recording under the 20 MB per-media limit of messages.
const MAX_VIDEO_SECONDS = 60
const VIDEO_BITS_PER_SECOND = 2_000_000

export default class extends Controller {
  static targets = [
    "dialog", "captureView", "viewfinder", "unavailable", "controls", "modeSwitch", "modeButton",
    "shutter", "shutterCore", "switchButton", "timer", "timerLabel",
    "previewView", "previewImage", "previewVideo", "caption", "draft"
  ]

  connect() {
    this.mode = "photo"
    this.facingMode = "user"
    this.stream = null
    this.recorder = null
    this.capturedFile = null
    this.previewUrl = null
    this.cameraRequest = 0
  }

  disconnect() {
    this.teardown()
  }

  get isInFlutterApp() {
    return typeof window.flutter_inappwebview !== "undefined"
  }

  // ===== Open / close =====

  // The dialog is shown before the camera is requested so the screen appears instantly;
  // the viewfinder fills in as soon as the stream is ready.
  open(event) {
    event?.preventDefault()
    if (this.dialogTarget.open) return

    this.captionTarget.value = this.hasDraftTarget ? this.draftTarget.value : ""
    this.facingMode = "user" // always opens on the front camera
    this._applyMode()
    this._showCaptureView()
    this.dialogTarget.showModal()
    this._startCamera()
  }

  close(event) {
    event?.preventDefault()
    if (this.dialogTarget.open) {
      this.dialogTarget.close() // the `close` event runs teardown
    } else {
      this.teardown()
    }
  }

  // Escape / Android back button: leave the preview first, then the camera.
  handleCancel(event) {
    event.preventDefault()
    if (this.capturedFile) {
      this.retake()
    } else {
      this.close()
    }
  }

  teardown() {
    this.cameraRequest++
    this._discardRecording()
    this._stopStream()
    this._clearPreview()
    this._showCaptureView()
  }

  // ===== Capture screen =====

  selectMode({params: {mode}}) {
    if (this._isRecording) return
    this.mode = mode
    this._applyMode()
  }

  switchCamera() {
    if (this._isRecording) return
    this.facingMode = this.facingMode === "user" ? "environment" : "user"
    this._startCamera()
  }

  shoot() {
    if (this.mode === "photo") {
      this._takePhoto()
    } else if (this._isRecording) {
      this.recorder.stop()
    } else {
      this._startVideo()
    }
  }

  pickFile(event) {
    const file = event.target.files[0]
    event.target.value = ""
    if (file) this._showPreview(file)
  }

  // ===== Preview screen =====

  // Closing the preview drops the capture and goes back to the camera.
  retake() {
    this._clearPreview()
    this._showCaptureView()

    if (this.stream?.active) {
      this.viewfinderTarget.play().catch(() => {})
    } else if (this.hasUnavailableTarget && this.unavailableTarget.classList.contains("hidden")) {
      this._startCamera()
    }
  }

  send() {
    if (!this.capturedFile) return

    this.dispatch("captured", {detail: {file: this.capturedFile, caption: this.captionTarget.value}})
    this.capturedFile = null
    this.close()
  }

  // ===== Camera stream =====

  async _startCamera() {
    const request = ++this.cameraRequest
    this._stopStream()
    this._setUnavailable(false)

    const stream = await this._requestStream()

    // The dialog was closed (or the camera switched again) while the stream was pending.
    if (request !== this.cameraRequest || !this.dialogTarget.open) {
      stream?.getTracks().forEach(track => track.stop())
      return
    }

    if (!stream) {
      this._setUnavailable(true)
      return
    }

    this.stream = stream
    this.viewfinderTarget.srcObject = stream
    this.viewfinderTarget.classList.toggle("-scale-x-100", this.facingMode === "user")
    this.viewfinderTarget.play().catch(() => {})
  }

  async _requestStream() {
    if (!navigator.mediaDevices?.getUserMedia) return null

    if (this.isInFlutterApp && !this.flutterPermissionsAsked) {
      const camera = await this._requestFlutterPermission("Camera")
      if (!camera) return null
      this.microphoneAllowed = await this._requestFlutterPermission("Microphone")
      this.flutterPermissionsAsked = true
    }

    const video = {facingMode: this.facingMode, width: {ideal: 1920}, height: {ideal: 1080}}

    // The microphone is only needed for videos: photos still work if it is refused.
    if (this.microphoneAllowed !== false) {
      try {
        return await navigator.mediaDevices.getUserMedia({video, audio: true})
      } catch (_error) {
        // Fall through to a video-only stream.
      }
    }

    try {
      return await navigator.mediaDevices.getUserMedia({video})
    } catch (error) {
      console.error("Error accessing camera:", error)
      return null
    }
  }

  _stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.hasViewfinderTarget) this.viewfinderTarget.srcObject = null
  }

  async _requestFlutterPermission(type) {
    try {
      const result = await window.flutter_inappwebview.callHandler(`request${type}Permission`)
      return result?.granted || result === true
    } catch (error) {
      console.error(`Error requesting ${type} permission:`, error)
      return true
    }
  }

  // ===== Photo =====

  _takePhoto() {
    const video = this.viewfinderTarget
    if (!this.stream || !video.videoWidth) return

    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext("2d")

    // The front camera is shown mirrored: the photo matches what the user saw.
    if (this.facingMode === "user") {
      context.translate(canvas.width, 0)
      context.scale(-1, 1)
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob((blob) => {
      if (blob) this._showPreview(new File([blob], `photo_${Date.now()}.jpg`, {type: "image/jpeg"}))
    }, "image/jpeg", 0.9)
  }

  // ===== Video =====

  get _isRecording() {
    return this.recorder?.state === "recording"
  }

  _startVideo() {
    if (!this.stream || typeof MediaRecorder === "undefined") return

    const mimeType = this._videoMimeType()
    const options = {videoBitsPerSecond: VIDEO_BITS_PER_SECOND}
    if (mimeType) options.mimeType = mimeType

    let recorder
    try {
      recorder = new MediaRecorder(this.stream, options)
    } catch (error) {
      console.error("Error starting video recording:", error)
      return
    }

    const chunks = []
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data)
    }
    recorder.onstop = () => {
      this._stopVideoTimer()
      this._setRecordingUI(false)
      if (this.recorder !== recorder) return // discarded

      this.recorder = null
      const type = (recorder.mimeType || mimeType || "video/webm").split(";")[0].trim()
      const extension = VIDEO_EXTENSIONS[type] || "webm"
      const blob = new Blob(chunks, {type})
      if (blob.size > 0) this._showPreview(new File([blob], `video_${Date.now()}.${extension}`, {type}))
    }

    this.recorder = recorder
    recorder.start()
    this._setRecordingUI(true)
    this._startVideoTimer()
  }

  _discardRecording() {
    const recorder = this.recorder
    this.recorder = null
    if (recorder && recorder.state !== "inactive") recorder.stop()
    this._stopVideoTimer()
    this._setRecordingUI(false)
  }

  _videoMimeType() {
    if (typeof MediaRecorder.isTypeSupported !== "function") return null
    return VIDEO_MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type)) || null
  }

  _startVideoTimer() {
    this.videoStartedAt = Date.now()
    this._renderTimer(0)
    this.videoTimer = setInterval(() => {
      const seconds = Math.floor((Date.now() - this.videoStartedAt) / 1000)
      this._renderTimer(seconds)
      if (seconds >= MAX_VIDEO_SECONDS && this._isRecording) this.recorder.stop()
    }, 250)
  }

  _stopVideoTimer() {
    if (this.videoTimer) {
      clearInterval(this.videoTimer)
      this.videoTimer = null
    }
  }

  _renderTimer(seconds) {
    const minutes = Math.floor(seconds / 60).toString().padStart(2, "0")
    const rest = (seconds % 60).toString().padStart(2, "0")
    this.timerLabelTarget.textContent = `${minutes}:${rest}`
  }

  // ===== UI state =====

  _applyMode() {
    this.modeButtonTargets.forEach((button) => {
      const active = button.dataset.mode === this.mode
      button.classList.toggle("bg-white", active)
      button.classList.toggle("text-black", active)
      button.setAttribute("aria-pressed", active)
    })
    this.shutterCoreTarget.classList.toggle("bg-white", this.mode === "photo")
    this.shutterCoreTarget.classList.toggle("bg-red-500", this.mode === "video")
  }

  _setRecordingUI(recording) {
    this._toggle(this.timerTarget, recording)
    this._toggle(this.modeSwitchTarget, !recording)
    this.switchButtonTarget.classList.toggle("invisible", recording)
    // Round red button while idle, red square while recording.
    this.shutterCoreTarget.classList.toggle("size-16", !recording)
    this.shutterCoreTarget.classList.toggle("rounded-full", !recording)
    this.shutterCoreTarget.classList.toggle("size-8", recording)
    this.shutterCoreTarget.classList.toggle("rounded-md", recording)
  }

  _setUnavailable(unavailable) {
    this._toggle(this.unavailableTarget, unavailable)
    this._toggle(this.controlsTarget, !unavailable)
    this.switchButtonTarget.classList.toggle("invisible", unavailable)
  }

  _showCaptureView() {
    this._toggle(this.captureViewTarget, true)
    this._toggle(this.previewViewTarget, false)
  }

  _showPreview(file) {
    this._clearPreview()
    this.capturedFile = file
    this.previewUrl = URL.createObjectURL(file)

    const isVideo = file.type.startsWith("video/")
    const media = isVideo ? this.previewVideoTarget : this.previewImageTarget
    media.src = this.previewUrl
    media.classList.remove("hidden")
    if (isVideo) this.previewVideoTarget.play().catch(() => {})

    this.viewfinderTarget.pause()
    this._toggle(this.captureViewTarget, false)
    this._toggle(this.previewViewTarget, true)
  }

  _clearPreview() {
    this.capturedFile = null
    if (this.hasPreviewVideoTarget) {
      this.previewVideoTarget.pause()
      this.previewVideoTarget.removeAttribute("src")
      this.previewVideoTarget.load()
      this.previewVideoTarget.classList.add("hidden")
    }
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.removeAttribute("src")
      this.previewImageTarget.classList.add("hidden")
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = null
    }
  }

  _toggle(element, visible) {
    element.classList.toggle("hidden", !visible)
    element.classList.toggle("flex", visible)
  }
}
